import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import { createSelector } from 'reselect'

import { getCurrentUser, getUserIsAdmin } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { postRetrieveCompanyTos } from '@/features/payroll/payrollActions'
import { selectPayrollProfile } from '@/features/payroll/payroll.selectors'
import { useHelloSignBAA } from '@/components/helloSignHelper'
import { GuidedOnboardingTaskIdents } from '@/features/onboarding/config'
import { getUserActionItemByActionItemIdentifier } from '@/features/user/dashboard/user-action-items/lib/userActionItems.selectors'
import { SignBAAAgreementStatus } from '@/features/user/dashboard/lib/userActionItemStatuses'
import { usePlaidConnectReminderTrigger } from '@/features/banking/plaid-connect-reminder'

export const selectShouldShowSmsBanner = createSelector(
  getCurrentUser,
  (currentUser) => {
    if (
      !currentUser ||
      currentUser?.admin ||
      currentUser?.smsLoginModalViewedAt === undefined
    )
      return false

    const { createdAt, smsLoginModalViewedAt } = currentUser
    const luxonCreatedAt = DateTime.fromISO(createdAt)
    const currentDate = DateTime.now()
    const differenceInDays = currentDate.diff(luxonCreatedAt, 'days').days

    return !smsLoginModalViewedAt && differenceInDays > 7
  }
)

export const selectShouldShowTosModal = createSelector(
  getCurrentUser,
  (currentUser) => {
    let localStorageOrUndefined

    try {
      localStorageOrUndefined = window.localStorage
    } catch {
      localStorageOrUndefined = undefined
    }

    const tosForceHide =
      localStorageOrUndefined?.getItem('tosForceHide') === 'true'

    return (
      !tosForceHide &&
      !currentUser?.admin &&
      Boolean(currentUser?.currentTosVersion) &&
      currentUser?.currentTosVersion !== currentUser?.tosVersion
    )
  }
)

export const useShouldShowGustoTOS = () => {
  const dispatch = useAppDispatch()
  const payrollProfile = useReselector(selectPayrollProfile)
  const [tosOutOfDate, setTosOutOfDate] = useState(false)

  useEffect(() => {
    const checkGustoTos = async () => {
      // Don't run check unless user is onboarded gusto user
      if (!payrollProfile?.onboardingCompleted) {
        return
      }
      const res = await dispatch(postRetrieveCompanyTos())

      if (res) {
        setTosOutOfDate(!res.latest_terms_accepted)
      }
    }

    checkGustoTos()
  }, [dispatch, payrollProfile?.onboardingCompleted])

  return tosOutOfDate
}

export const useShouldShowBaaModal = () => {
  const { baaSigned, fetching } = useHelloSignBAA()
  const signBaaActionItem = useReselector(
    getUserActionItemByActionItemIdentifier,
    GuidedOnboardingTaskIdents.SIGN_BAA_AGREEMENT
  )
  const isAdmin = useReselector(getUserIsAdmin)

  return Boolean(
    !isAdmin &&
    !fetching &&
    !baaSigned &&
    signBaaActionItem &&
    signBaaActionItem.status !== SignBAAAgreementStatus.signed &&
    !signBaaActionItem.completedAt
  )
}

export enum TOS_MODAL {
  HEARD_TOS = 'HEARD_TOS',
  BAA_MODAL = 'BAA_MODAL',
  GUSTO_TOS = 'GUSTO_TOS',
  PLAID_CONNECT_REMINDER = 'PLAID_CONNECT_REMINDER',
}

export const useBaseRouterModalToShow = () => {
  const [dismissedModals, setDismissedModals] = useState<Record<string, true>>(
    {}
  )

  const shouldShowTosModal = useReselector(selectShouldShowTosModal)
  const shouldShowBaaModal = useShouldShowBaaModal()
  const shouldShowGustoTOS = useShouldShowGustoTOS()
  const { shouldShow: shouldShowPlaidReminder, dismiss: dismissPlaidReminder } =
    usePlaidConnectReminderTrigger()

  const dismissModal = useCallback(
    (modalId: TOS_MODAL) => {
      if (modalId === TOS_MODAL.PLAID_CONNECT_REMINDER) {
        dismissPlaidReminder()
        return
      }
      setDismissedModals((prev) => ({
        ...prev,
        [modalId]: true,
      }))
    },
    [dismissPlaidReminder]
  )

  const baseModalToShow = useMemo(() => {
    if (shouldShowTosModal && !dismissedModals[TOS_MODAL.HEARD_TOS]) {
      return TOS_MODAL.HEARD_TOS
    } else if (shouldShowBaaModal && !dismissedModals[TOS_MODAL.BAA_MODAL]) {
      return TOS_MODAL.BAA_MODAL
    } else if (shouldShowGustoTOS && !dismissedModals[TOS_MODAL.GUSTO_TOS]) {
      return TOS_MODAL.GUSTO_TOS
    } else if (shouldShowPlaidReminder) {
      return TOS_MODAL.PLAID_CONNECT_REMINDER
    }
    return null
  }, [
    dismissedModals,
    shouldShowBaaModal,
    shouldShowGustoTOS,
    shouldShowPlaidReminder,
    shouldShowTosModal,
  ])

  return {
    baseModalToShow,
    dismissModal,
  }
}
